export interface ChapterSection {
  type: 'scene' | 'inter-scene';
  id: string;
  sceneId?: string;
  html: string;
}

const LEGACY_SEPARATOR = /<p(?:\s[^>]*)?>\s*(?:<br\s*\/?\s*>)?\s*<\/p>\s*<p(?:\s[^>]*)?>\s*(?:<br\s*\/?\s*>)?\s*<\/p>/gi;

export function renderChapterSections(sections: ChapterSection[]): string {
  const normalized = sections.flatMap((section, index): ChapterSection[] => {
    const previous = sections[index - 1];
    if (section.type === 'scene' && previous?.type === 'scene') {
      const id = 'inter-' + previous.id + '-' + section.id;
      return [{ type: 'inter-scene', id, html: '<p></p><p></p>' }, section];
    }
    return [section];
  });
  return normalized.map((section) => {
    const id = section.sceneId || section.id;
    const kind = section.type === 'scene' ? 'data-scene-id' : 'data-section-id';
    return '<section data-section-type=' + section.type + ' ' + kind + '=' + id + '>' + section.html + '</section>';
  }).join('');
}

export function parseChapterSections(html: string): ChapterSection[] {
  if (!html) return [];
  const document = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(document.body.querySelectorAll(':scope > section')).flatMap((element): ChapterSection[] => {
    const type = element.getAttribute('data-section-type');
    const sceneId = element.getAttribute('data-scene-id') || undefined;
    const id = sceneId || element.getAttribute('data-section-id');
    const content = element.querySelector(':scope > .scene-section-content');
    const sectionHtml = content?.innerHTML || element.innerHTML;
    return id && (type === 'scene' || type === 'inter-scene') ? [{ type, id, sceneId, html: sectionHtml }] : [];
  });
}

export function ensureStructuredChapterHtml(html: string, sceneIds: string[]): string {
  const existing = parseChapterSections(html);
  if (existing.length) return renderChapterSections(existing);
  const parts = html ? html.split(LEGACY_SEPARATOR) : [];
  return renderChapterSections(sceneIds.map((sceneId, index) => ({ type: 'scene', id: sceneId, sceneId, html: parts[index] || '<p></p>' })));
}

export function sceneSectionMap(html: string): Record<string, string> {
  return Object.fromEntries(parseChapterSections(html).filter((section) => section.type === 'scene' && section.sceneId).map((section) => [section.sceneId!, section.html]));
}

export function replaceSceneSections(currentHtml: string, replacements: Record<string, string>): string {
  const sections = parseChapterSections(currentHtml);
  const used = new Set<string>();
  const replaced = sections.flatMap((section): ChapterSection[] => {
    if (section.type === 'inter-scene') return [section];
    if (!section.sceneId || replacements[section.sceneId] === undefined) return [];
    used.add(section.sceneId);
    return [{ ...section, html: replacements[section.sceneId] }];
  });
  for (const [sceneId, html] of Object.entries(replacements)) {
    if (!used.has(sceneId)) replaced.push({ type: 'scene', id: sceneId, sceneId, html });
  }
  return renderChapterSections(replaced);
}
